import {includes} from '@shared/helpers/collection'
import {toNumberString} from '@shared/helpers/format'
import {eq, isEmpty} from '@shared/helpers/lang'

import {
    Card,
    CashReceiptIssueType,
    CashReceiptIssueTypeType,
    CashReceiptIssueUsage,
    CashReceiptIssueUsageType,
    PayMethod,
    ReceiptAdditionalPolicy,
} from '@web/core/constants'

import type {CardType, PayMethodType, ReceiptAdditionalPolicyType} from '@web/core/constants'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ValidationNumber = {
    PHONE_NUMBER: 'PHONE_NUMBER',
    SOCIAL_NUMBER: 'SOCIAL_NUMBER',
    CASH_RECEIPT_CARD_NUMBER: 'CASH_RECEIPT_CARD_NUMBER',
    BUSINESS_REGISTRATION_NUMBER: 'BUSINESS_REGISTRATION_NUMBER',
} as const

type MinimumCount = number
type ValidationNumberType = Constant<typeof ValidationNumber>

const ValidationMinimumCount: Record<ValidationNumberType, MinimumCount[]> = {
    PHONE_NUMBER: [3, 3, 4],
    SOCIAL_NUMBER: [6, 7],
    CASH_RECEIPT_CARD_NUMBER: [4, 4, 4, 1],
    BUSINESS_REGISTRATION_NUMBER: [3, 2, 5],
}

/**
 * type 과 number 배열을 받아, 검증합니다
 * - '010-1234-1234' 는 split('-') 으로 배열로 넣도록 유도합니다
 */
const isValidNumbers = ({type, numbers}: {type: ValidationNumberType; numbers: string[]}) => {
    if (!ValidationMinimumCount[type] || ValidationMinimumCount[type].length !== numbers.length) {
        return false
    }
    return numbers.every((value, index) => {
        if (!value || (ValidationMinimumCount[type][index] && value.length < ValidationMinimumCount[type][index])) {
            return false
        }
        const numberCheck = toNumberString(value)
        return numberCheck && numberCheck.length === value.length
    })
}

export const getCashReceiptNumbers = ({
    residentRegistrationNumberFirst,
    residentRegistrationNumberSecond,
    mobilePhoneNumberFirst,
    mobilePhoneNumberSecond,
    mobilePhoneNumberThird,
    cashReceiptCardNumberFirst,
    cashReceiptCardNumberSecond,
    cashReceiptCardNumberThird,
    cashReceiptCardNumberFourth,
    businessRegistrationNumberFirst,
    businessRegistrationNumberSecond,
    businessRegistrationNumberThird,
}: {
    residentRegistrationNumberFirst: string
    residentRegistrationNumberSecond: string
    mobilePhoneNumberFirst: string
    mobilePhoneNumberSecond: string
    mobilePhoneNumberThird: string
    cashReceiptCardNumberFirst: string
    cashReceiptCardNumberSecond: string
    cashReceiptCardNumberThird: string
    cashReceiptCardNumberFourth: string
    businessRegistrationNumberFirst: string
    businessRegistrationNumberSecond: string
    businessRegistrationNumberThird: string
}) => {
    return {
        residentRegistrationNumber: [residentRegistrationNumberFirst, residentRegistrationNumberSecond].join('-'),
        mobilePhoneNumber: [mobilePhoneNumberFirst, mobilePhoneNumberSecond, mobilePhoneNumberThird].join('-'),
        cashReceiptCardNumber: [
            cashReceiptCardNumberFirst,
            cashReceiptCardNumberSecond,
            cashReceiptCardNumberThird,
            cashReceiptCardNumberFourth,
        ].join('-'),
        businessRegistrationNumber: [
            businessRegistrationNumberFirst,
            businessRegistrationNumberSecond,
            businessRegistrationNumberThird,
        ].join('-'),
        // TODO masking 처리 확인
        maskedResidentRegistrationNumber: `${residentRegistrationNumberFirst}-*******`,
        maskedCashReceiptCardNumber: `${cashReceiptCardNumberFirst}-****-****-****`,
        maskedBusinessRegistrationNumber: `${businessRegistrationNumberFirst}-**-*****`,
    }
}

/**
 * 현금영수증 발행 정책 (아래에 OR 조건 나열)
 * 1. 선택된 결제수단이 현금관련 결제수단일 경우
 * 2. 충전포인트 + 충전 후 사용할 페이머니 + 사용하는 현금성포인트 > 0 인 경우
 * 3. 후불결제 사용 시
 * 4. 카드간편결제 > 네이버페이머니카드 선택 시
 */
export const isCashReceiptAvailable = ({
    selectedPayMethod,
    bankEasyPayAccounts,
    totalChargePointUsedAmount,
    payLaterUsed,
    usingCashRewardPoint,
    receiptPolicy,
    selectedCardType,
}: {
    /** 선택된 결제수단 */
    selectedPayMethod: PayMethodType
    /** 계좌간편결제 계좌들 */
    bankEasyPayAccounts: object[]
    /** 충전포인트 + 충전 후 사용할 페이머니 */
    totalChargePointUsedAmount: number
    /** 후불결제 사용여부 */
    payLaterUsed?: boolean
    /** 사용하는 적립포인트 내 현금성포인트 */
    usingCashRewardPoint?: number
    /** 현금영수증 추가정책 */
    receiptPolicy: ReceiptAdditionalPolicyType[]
    /** 선택된 카드간편결제카드의 종류 */
    selectedCardType?: CardType
}) => {
    const paymentMethodsUsingCash: PayMethodType[] = [
        PayMethod.CULTURE_LAND,
        PayMethod.HAPPY_MONEY,
        PayMethod.VIRTUAL_ACCOUNT,
    ]
    const isUsingCash =
        paymentMethodsUsingCash.includes(selectedPayMethod) ||
        (eq(PayMethod.BANK_EASY_PAY, selectedPayMethod) && !isEmpty(bankEasyPayAccounts))

    const pointChargeWithCashUsed = totalChargePointUsedAmount + (usingCashRewardPoint ?? 0) > 0

    const isPaymoneyCardSelected =
        eq(selectedPayMethod, PayMethod.CARD_EASY_PAY) &&
        receiptPolicy.includes(ReceiptAdditionalPolicy.NFC_PREPAID) &&
        eq(selectedCardType, Card.NFC_PREPAID)

    return (
        isUsingCash ||
        paymentMethodsUsingCash.includes(selectedPayMethod) ||
        pointChargeWithCashUsed ||
        payLaterUsed ||
        isPaymoneyCardSelected
    )
}

/** issueType에 따라 개인소득공제용 / 사업자증빙용 구분하는 함수 */
export const getUsageType = (issueType: CashReceiptIssueTypeType): CashReceiptIssueUsageType => {
    if (
        includes(issueType, [
            CashReceiptIssueType.PR_CELL_PHONE_NUMBER,
            CashReceiptIssueType.PR_CASH_RECEIPT_CARD_NUMBER,
        ])
    ) {
        return CashReceiptIssueUsage.PERSONAL_INCOME_TAX
    }
    return CashReceiptIssueUsage.BIZ_EXPENSE_PROOF
}

export const isValidIssueNumber = ({
    issueType,
    issueNumber,
}: {
    issueType: CashReceiptIssueTypeType
    issueNumber: string[]
}) => {
    const convertMap: Record<CashReceiptIssueTypeType, ValidationNumberType> = {
        PR_CELL_PHONE_NUMBER: 'PHONE_NUMBER',
        PR_SOCIAL_NUMBER: 'SOCIAL_NUMBER',
        PR_CASH_RECEIPT_CARD_NUMBER: 'CASH_RECEIPT_CARD_NUMBER',
        BUSINESS_REGISTRATION_NUMBER: 'BUSINESS_REGISTRATION_NUMBER',
    }

    return isValidNumbers({type: convertMap[issueType], numbers: issueNumber})
}

/**
 * 자동결제 현금영수증 발행 정책 (아래에 OR 조건 나열)
 * 1. 선택된 결제수단이 현금관련 결제수단일 경우 (충전결제 선택 포함)
 * 2. 포인트 우선사용 사용 경우 / 포인트 0원 이상 사용 여부
 */
export const isPaymentCashReceiptAvailable = ({
    selectedPayMethod,
    isUsingPoint,
}: {
    /** 선택된 결제수단 */
    selectedPayMethod?: PayMethodType
    /** 포인트 사용 여부 */
    isUsingPoint: boolean
}) => {
    const paymentMethodsUsingCash: PayMethodType[] = [
        PayMethod.BANK_EASY_PAY,
        PayMethod.RECHARGE_POINT,
        PayMethod.CULTURE_LAND,
        PayMethod.HAPPY_MONEY,
        PayMethod.VIRTUAL_ACCOUNT,
        PayMethod.RECHARGE_POINT,
    ]

    return (selectedPayMethod && paymentMethodsUsingCash.includes(selectedPayMethod)) || isUsingPoint
}

export const CashReceiptNumberLengths = {
    [CashReceiptIssueType.PR_CELL_PHONE_NUMBER]: [
        [3, 4, 4],
        [3, 3, 4],
    ],
    [CashReceiptIssueType.PR_CASH_RECEIPT_CARD_NUMBER]: [[4, 4, 4, 7]],
    [CashReceiptIssueType.BUSINESS_REGISTRATION_NUMBER]: [[3, 2, 5]],
}

export const splitNumber = (number: string, lengthPatterns: number[][]) => {
    const splitByPattern = (pattern: number[]) => {
        return pattern.reduce<{parts: string[]; start: number}>(
            (acc, length) => {
                const part = number.slice(acc.start, acc.start + length)
                acc.parts.push(part || '') // 빈 문자열 처리
                acc.start += length
                return acc
            },
            {parts: [], start: 0},
        ).parts
    }

    for (const lengths of lengthPatterns) {
        if (number.length === lengths.reduce((sum, length) => sum + length, 0)) {
            return splitByPattern(lengths)
        }
    }

    // 현금영수증 자릿수가 완전하지 않은 경우 기본값으로 자른다
    return splitByPattern(lengthPatterns[0] ?? [])
}
